/**
 * ActivatePage — landed via the activation URL admins share with new invitees.
 *
 * URL shape: /activate/:token  (tenant subdomain in the host carries the workspace)
 *
 * Flow:
 *   1. On mount: GET /auth/activate/check?token=... → display name + email + tenant
 *   2. User picks a password (8+ chars, confirm field)
 *   3. POST /auth/activate {token, new_password} → 204
 *   4. Redirect to /login (the LoginPage detects ?activated=1 and shows a banner)
 *
 * No auth required for this page; it's the only way to enter your password.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { LogoMark, LogoWordmark } from "@m2s/design-system";

import { api, extractErrorMessage } from "@/lib/api";

type CheckResponse = {
  email: string;
  display_name: string;
  tenant_name: string;
};

/** The workspace slug (`tslug` claim) from the activation JWT — read only to
 * build the sign-in link; the server still verifies the token itself. */
function workspaceFromToken(token: string | undefined): string | null {
  try {
    const part = token?.split(".")[1];
    if (!part) return null;
    const claims = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof claims.tslug === "string" ? claims.tslug : null;
  } catch {
    return null;
  }
}

function errorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null)?.response?.status;
}

export function ActivatePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const workspace = workspaceFromToken(token);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const check = useQuery({
    queryKey: ["activate-check", token],
    queryFn: async () =>
      (await api.get<CheckResponse>(`/auth/activate/check?token=${token}`)).data,
    enabled: !!token,
    retry: false,
  });

  const activate = useMutation({
    mutationFn: async () => {
      await api.post("/auth/activate", { token, new_password: password });
    },
    onSuccess: () => {
      // Land on login with a banner. LoginPage reads ?activated=1.
      navigate("/login?activated=1", { replace: true });
    },
    onError: (err) => {
      setError(
        extractErrorMessage(
          err,
          "Could not activate. Try again or ask your admin for a new invite.",
        ),
      );
    },
  });

  useEffect(() => {
    setError(null);
  }, [password, confirm]);

  return (
    <main style={pageMain}>
      <div style={card}>
        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <LogoMark size={56} withDial animated />
          </div>
          <LogoWordmark size={22} />
        </div>

        {check.isLoading ? (
          <p style={{ color: "var(--mute)", fontSize: 13, textAlign: "center" }}>
            Checking your invite…
          </p>
        ) : check.isError && errorStatus(check.error) === 409 ? (
          // The account behind this link already has a password — e.g. the
          // link was already used, or the person had an account before this
          // invite. Nothing to set up: send them to sign in to the workspace.
          <div style={infoCard}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Your account is already set up.
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              There's no password to create. Sign in with your existing password
              {workspace ? (
                <>
                  {" "}
                  to open the <strong>{workspace}</strong> workspace
                </>
              ) : null}
              . If you've forgotten it, use Forgot password on the sign-in page.
            </div>
            <button
              type="button"
              onClick={() =>
                navigate(workspace ? `/login?workspace=${encodeURIComponent(workspace)}` : "/login")
              }
              style={{ ...primaryBtn, width: "100%", marginTop: 14, cursor: "pointer" }}
            >
              Go to sign in
            </button>
          </div>
        ) : check.isError && errorStatus(check.error) === 404 ? (
          <div style={alertCard}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              This invite is no longer valid.
            </div>
            <div style={{ fontSize: 13 }}>
              The workspace or account it was for no longer exists. Ask your admin to
              send a new one.
            </div>
          </div>
        ) : check.isError ? (
          <div style={alertCard}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              This invite isn't valid.
            </div>
            <div style={{ fontSize: 13 }}>
              The link may have expired (they last 24 hours) or been copied incompletely.
              Ask your admin to send a new one.
            </div>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 500, margin: "16px 0 4px", color: "var(--body)" }}>
              Welcome, {check.data?.display_name}.
            </h1>
            <p style={{ fontSize: 13, color: "var(--mute)", margin: "0 0 24px" }}>
              Set a password to finish joining{" "}
              <strong style={{ color: "var(--body)" }}>{check.data?.tenant_name}</strong>.
              <br />
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                {check.data?.email}
              </span>
            </p>

            <label style={fieldRow}>
              <span style={fieldLabel}>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={input}
                minLength={8}
              />
              <span style={hint}>At least 8 characters.</span>
            </label>

            <label style={fieldRow}>
              <span style={fieldLabel}>Confirm password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={input}
                minLength={8}
              />
            </label>

            {error && (
              <p role="alert" style={alertText}>
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={() => {
                if (password.length < 8) {
                  setError("Password must be at least 8 characters.");
                  return;
                }
                if (password !== confirm) {
                  setError("Passwords don't match.");
                  return;
                }
                activate.mutate();
              }}
              disabled={activate.isPending}
              style={{
                ...primaryBtn,
                width: "100%",
                marginTop: 16,
                opacity: activate.isPending ? 0.6 : 1,
                cursor: activate.isPending ? "wait" : "pointer",
              }}
            >
              {activate.isPending ? "Activating…" : "Activate account"}
            </button>
          </>
        )}
      </div>
    </main>
  );
}

const pageMain: CSSProperties = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  background: "var(--canvas)",
  padding: 32,
};

const card: CSSProperties = {
  width: "100%",
  maxWidth: 420,
  padding: 32,
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
};

const fieldRow: CSSProperties = { display: "block", marginBottom: 12 };

const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "10px 12px",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const hint: CSSProperties = {
  display: "block",
  marginTop: 6,
  fontSize: 12,
  color: "var(--mute)",
};

const primaryBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "11px 16px",
  fontSize: 14,
  fontWeight: 500,
};

const infoCard: CSSProperties = {
  padding: "16px 18px",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  color: "var(--body)",
};

const alertCard: CSSProperties = {
  padding: "16px 18px",
  background: "rgba(238,29,54,0.06)",
  border: "1px solid rgba(238,29,54,0.3)",
  borderRadius: 6,
  color: "var(--body)",
};

const alertText: CSSProperties = {
  marginTop: 12,
  marginBottom: 0,
  padding: "8px 12px",
  fontSize: 13,
  color: "#ee1d36",
  background: "rgba(238,29,54,0.06)",
  border: "1px solid rgba(238,29,54,0.3)",
  borderRadius: 4,
};
